import React, { useState, useEffect } from 'react';
import { Code, Copy, Check } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

import { API_BASE as API } from '@/utils/api';

const sizes = {
  small: { width: 280, height: 180 },
  medium: { width: 320, height: 220 },
  large: { width: 400, height: 280 }
};

export const AddWidgetModal = ({ isOpen, onClose, currentMood }) => {
  const [widgetSize, setWidgetSize] = useState('medium');
  const [embedCode, setEmbedCode] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (isOpen) {
      const size = sizes[widgetSize];
      const widgetUrl = `https://moodofthe.world/api/widget`;
      setEmbedCode(`<iframe 
  src="${widgetUrl}" 
  width="${size.width}" 
  height="${size.height}" 
  frameborder="0" 
  style="border-radius: 16px; overflow: hidden; box-shadow: 0 4px 20px rgba(0,0,0,0.3);"
  title="Mood of the World Widget"
  loading="lazy">
</iframe>`);
    }
  }, [isOpen, widgetSize]);

  const copyCode = async () => {
    await navigator.clipboard.writeText(embedCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="apple-card border-0 max-w-2xl w-[95vw] max-h-[85vh] overflow-y-auto modal-mobile-optimized modal-glow">
        <DialogHeader>
          <DialogTitle className="apple-headline flex items-center gap-2">
            <Code size={20} />
            Add Widget to Your Website
          </DialogTitle>
        </DialogHeader>
        
        <div className="mt-4 space-y-6">
          {/* Size Selection */}
          <div>
            <label className="text-sm font-medium mb-2 block" style={{ color: 'var(--apple-text-secondary)' }}>
              Widget Size
            </label>
            <div className="flex gap-2">
              {Object.keys(sizes).map(size => (
                <button
                  key={size}
                  onClick={() => setWidgetSize(size)}
                  className={`px-4 py-2 rounded-lg text-sm capitalize transition-all ${
                    widgetSize === size 
                      ? 'bg-blue-500 text-white' 
                      : 'bg-white/5 hover:bg-white/10'
                  }`}
                  style={{ color: widgetSize === size ? 'white' : 'var(--apple-text-secondary)' }}
                >
                  {size} ({sizes[size].width}x{sizes[size].height})
                </button>
              ))}
            </div>
          </div>

          {/* Preview */}
          <div>
            <label className="text-sm font-medium mb-2 block" style={{ color: 'var(--apple-text-secondary)' }}>
              Preview
            </label>
            <div className="flex justify-center p-4 rounded-xl" style={{ background: 'var(--apple-bg-secondary)' }}>
              <iframe
                src={`${API}/api/widget`}
                width={sizes[widgetSize].width}
                height={sizes[widgetSize].height}
                frameBorder="0"
                style={{ borderRadius: 16, overflow: 'hidden' }}
                title="Widget Preview"
              />
            </div>
          </div>

          {/* Embed Code */}
          <div>
            <label className="text-sm font-medium mb-2 block" style={{ color: 'var(--apple-text-secondary)' }}>
              Embed Code
            </label>
            <div className="relative">
              <pre 
                className="rounded-xl p-4 text-xs overflow-auto" 
                style={{ 
                  background: 'var(--apple-bg-tertiary)', 
                  color: 'var(--apple-text-secondary)',
                  maxHeight: 120 
                }}
              >
                {embedCode}
              </pre>
              <button 
                onClick={copyCode}
                className="absolute top-2 right-2 p-2 rounded-lg hover:bg-white/10 transition-colors"
                title="Copy to clipboard"
              >
                {copied ? <Check size={16} className="text-green-400" /> : <Copy size={16} />}
              </button>
            </div>
          </div>

          {/* Copy Button */}
          <button 
            onClick={copyCode} 
            className="apple-button apple-button-primary w-full"
            data-testid="copy-widget-code-btn"
          >
            {copied ? (
              <>
                <Check size={16} className="inline mr-2" />
                Copied to Clipboard!
              </>
            ) : (
              <>
                <Copy size={16} className="inline mr-2" />
                Copy Embed Code
              </>
            )}
          </button>

          {/* Instructions */}
          <div className="text-xs space-y-1" style={{ color: 'var(--apple-text-tertiary)' }}>
            <p>📋 Paste this code into your website HTML where you want the widget to appear.</p>
            <p>🔄 The widget auto-refreshes every 10 minutes to show the latest global mood.</p>
            <p>🎨 The widget adapts its colors based on the current world mood.</p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
